import { Injectable } from '@angular/core';
import {
  RegisterMemberMarketingDashboardDto,
  RegisterMemberMonthlySeriesDto,
  RegisterMemberMonthlyVehicleTypeSeriesDto,
  RegisterMemberSummaryDto,
  RegisterMemberVehicleTypeBreakdownDto,
} from '../../models/cityparking/register-member-list.model';
import {
  RegisterMemberMarketingKpiCard,
  RegisterMemberMarketingMonth,
  RegisterMemberMarketingResult,
  RegisterMemberMarketingSummary,
  RegisterMemberMarketingTrendInsight,
  RegisterMemberMarketingVehicleType,
  RegisterMemberMarketingVehicleTypeSeries,
  RegisterMemberTrendDirection,
} from '../../models/cityparking/register-member-marketing.model';

const VEHICLE_TYPE_COLORS = [
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#f43f5e',
  '#0284c7',
  '#14b8a6',
  '#84cc16',
  '#64748b',
];

@Injectable({ providedIn: 'root' })
export class MapperRegisterMemberMarketingService {
  mapDashboard(dto: unknown): RegisterMemberMarketingResult {
    const raw = (dto ?? {}) as RegisterMemberMarketingDashboardDto & Record<string, unknown>;
    const summary = this.mapSummary(
      (raw.summary ?? raw['Summary']) as RegisterMemberSummaryDto | Record<string, unknown>,
    );
    const vehicleTypeBreakdown = this.mapVehicleTypes(
      (raw.vehicleTypeBreakdown ?? raw['VehicleTypeBreakdown'] ?? []) as unknown[],
    );
    const monthlyFlat = (raw.monthlyByVehicleType ?? raw['MonthlyByVehicleType'] ?? []) as unknown[];
    const monthlyRaw = (raw.monthlySeries ?? raw['MonthlySeries'] ?? []) as unknown[];
    const monthlySeries = this.mapMonthly(monthlyRaw, summary, monthlyFlat, vehicleTypeBreakdown);
    const vehicleTypeMonthlySeries = this.mapVehicleTypeMonthly(
      monthlyFlat,
      monthlySeries,
      vehicleTypeBreakdown,
    );
    const chartMaxPaid = Math.max(1, ...monthlySeries.map((m) => m.paid));

    // เดือนปัจจุบันยังไม่จบ — ไม่ใช้ MoM จาก API ใน KPI
    if (monthlySeries.some((m) => m.isIncompleteMonth)) {
      const lastCompleted = [...monthlySeries].reverse().find((m) => !m.isIncompleteMonth && m.momChangePercent !== null);
      summary.momChangePercent = lastCompleted?.momChangePercent ?? null;
    }

    return {
      summary,
      monthlySeries,
      vehicleTypeBreakdown,
      vehicleTypeMonthlySeries,
      vehicleTypeDonutGradient: this.buildVehicleTypeDonut(vehicleTypeBreakdown, summary.paidCount),
      kpiCards: this.buildKpiCards(summary, vehicleTypeBreakdown),
      trendInsight: this.buildTrendInsight(monthlySeries),
      chartMaxPaid,
      siteName: String(raw.siteName ?? raw['SiteName'] ?? '').trim(),
      siteAddress: String(raw.siteAddress ?? raw['SiteAddress'] ?? '').trim(),
      siteTaxId: String(raw.siteTaxId ?? raw['SiteTaxId'] ?? '').trim(),
    };
  }

  private mapSummary(raw: RegisterMemberSummaryDto | Record<string, unknown>): RegisterMemberMarketingSummary {
    const s = (raw ?? {}) as RegisterMemberSummaryDto & Record<string, unknown>;
    const mom = s.momChangePercent ?? s['MomChangePercent'];
    const paidCount = Number(s.paidCount ?? s['PaidCount'] ?? 0);
    return {
      totalCount: Number(s.totalCount ?? s['TotalCount'] ?? paidCount),
      unpaidCount: Number(s.unpaidCount ?? s['UnpaidCount'] ?? 0),
      paidCount,
      cancelledCount: Number(s.cancelledCount ?? s['CancelledCount'] ?? 0),
      expiredUnpaidCount: Number(s.expiredUnpaidCount ?? s['ExpiredUnpaidCount'] ?? 0),
      conversionPercent: Number(s.conversionPercent ?? s['ConversionPercent'] ?? 0),
      peakMonthLabel: String(s.peakMonthLabel ?? s['PeakMonthLabel'] ?? ''),
      peakMonthTotal: Number(s.peakMonthTotal ?? s['PeakMonthTotal'] ?? 0),
      lowestMonthLabel: String(s.lowestMonthLabel ?? s['LowestMonthLabel'] ?? ''),
      lowestMonthTotal: Number(s.lowestMonthTotal ?? s['LowestMonthTotal'] ?? 0),
      momChangePercent: mom === null || mom === undefined ? null : Number(mom),
    };
  }

  private mapMonthly(
    items: unknown[],
    summary: RegisterMemberMarketingSummary,
    monthlyFlatRaw: unknown[],
    vehicleTypes: RegisterMemberMarketingVehicleType[],
  ): RegisterMemberMarketingMonth[] {
    const flat = monthlyFlatRaw.map((it) => {
      const r = (it ?? {}) as RegisterMemberMonthlyVehicleTypeSeriesDto & Record<string, unknown>;
      return {
        year: Number(r.year ?? r['Year'] ?? 0),
        month: Number(r.month ?? r['Month'] ?? 0),
        vehicleTypeId: Number(r.vehicleTypeId ?? r['VehicleTypeId'] ?? 0),
        vehicleTypeName: String(r.vehicleTypeName ?? r['VehicleTypeName'] ?? 'ไม่ระบุประเภท'),
        total: Number(r.total ?? r['Total'] ?? 0),
      };
    });

    const colorByKey = new Map(
      vehicleTypes.map((v) => [`${v.vehicleTypeId}|${v.vehicleTypeName}`, v.color] as const),
    );

    const maxPaid = Math.max(
      1,
      ...items.map((it) => {
        const r = (it ?? {}) as RegisterMemberMonthlySeriesDto & Record<string, unknown>;
        return Number(r.paid ?? r['Paid'] ?? r.total ?? r['Total'] ?? 0);
      }),
    );

    return items.map((it, index) => {
      const r = (it ?? {}) as RegisterMemberMonthlySeriesDto & Record<string, unknown>;
      const year = Number(r.year ?? r['Year'] ?? 0);
      const month = Number(r.month ?? r['Month'] ?? 0);
      const label = String(r.label ?? r['Label'] ?? `${month}/${year}`);
      const paid = Number(r.paid ?? r['Paid'] ?? r.total ?? r['Total'] ?? 0);
      const monthKey = `${String(month).padStart(2, '0')}/${year}`;

      let prevPaid: number | null = null;
      let momDelta: number | null = null;
      let momChangePercent: number | null = null;
      let trend: RegisterMemberTrendDirection | null = null;
      const isIncompleteMonth = this.isIncompleteCalendarMonth(year, month);

      if (index > 0) {
        const prev = items[index - 1] as RegisterMemberMonthlySeriesDto & Record<string, unknown>;
        prevPaid = Number(prev.paid ?? prev['Paid'] ?? prev.total ?? prev['Total'] ?? 0);
        if (isIncompleteMonth) {
          // เดือนปัจจุบันยังไม่จบ — คำนวณผลต่างได้แต่ห้ามตัดสินทิศทาง
          momDelta = paid - prevPaid;
          momChangePercent = null;
          trend = 'pending';
        } else {
          momDelta = paid - prevPaid;
          momChangePercent = this.calcChangePercent(prevPaid, paid);
          trend = this.directionFromDelta(momDelta);
        }
      } else if (isIncompleteMonth) {
        trend = 'pending';
      }

      const monthFlat = flat.filter((f) => f.year === year && f.month === month);
      const segments = (monthFlat.length > 0 ? monthFlat : [])
        .map((f) => ({
          vehicleTypeId: f.vehicleTypeId,
          vehicleTypeName: f.vehicleTypeName,
          count: f.total,
          pct: paid > 0 ? Math.round((f.total / paid) * 100) : 0,
          color:
            colorByKey.get(`${f.vehicleTypeId}|${f.vehicleTypeName}`) ??
            VEHICLE_TYPE_COLORS[Math.abs(f.vehicleTypeId) % VEHICLE_TYPE_COLORS.length],
        }))
        .filter((s) => s.count > 0)
        .sort((a, b) => b.count - a.count);

      return {
        year,
        month,
        label,
        paid,
        paidPct: Math.round((paid / maxPaid) * 100),
        isPeak: !!summary.peakMonthLabel && monthKey === summary.peakMonthLabel && paid === summary.peakMonthTotal,
        isLowest:
          !!summary.lowestMonthLabel &&
          monthKey === summary.lowestMonthLabel &&
          paid === summary.lowestMonthTotal,
        isIncompleteMonth,
        prevPaid,
        momDelta,
        momChangePercent,
        trend,
        segments,
      };
    });
  }

  private mapVehicleTypes(items: unknown[]): RegisterMemberMarketingVehicleType[] {
    const mapped = items.map((it, index) => {
      const r = (it ?? {}) as RegisterMemberVehicleTypeBreakdownDto & Record<string, unknown>;
      return {
        vehicleTypeId: Number(r.vehicleTypeId ?? r['VehicleTypeId'] ?? 0),
        vehicleTypeName: String(r.vehicleTypeName ?? r['VehicleTypeName'] ?? 'ไม่ระบุประเภท'),
        count: Number(r.count ?? r['Count'] ?? r.paid ?? r['Paid'] ?? 0),
        percent: Number(r.percent ?? r['Percent'] ?? 0),
        barPct: 0,
        color: VEHICLE_TYPE_COLORS[index % VEHICLE_TYPE_COLORS.length],
        donutStartPct: 0,
        donutEndPct: 0,
      };
    });
    const max = Math.max(1, ...mapped.map((x) => x.count));
    let cursor = 0;
    return mapped.map((x) => {
      const start = cursor;
      const end = cursor + x.percent;
      cursor = end;
      return {
        ...x,
        barPct: Math.round((x.count / max) * 100),
        donutStartPct: start,
        donutEndPct: end,
      };
    });
  }

  private buildVehicleTypeDonut(
    types: RegisterMemberMarketingVehicleType[],
    paidCount: number,
  ): string {
    if (paidCount <= 0 || types.length === 0) {
      return 'conic-gradient(#e2e8f0 0% 100%)';
    }
    const parts = types.map((t) => `${t.color} ${t.donutStartPct}% ${t.donutEndPct}%`);
    return `conic-gradient(${parts.join(', ')})`;
  }

  private mapVehicleTypeMonthly(
    items: unknown[],
    months: RegisterMemberMarketingMonth[],
    breakdown: RegisterMemberMarketingVehicleType[],
  ): RegisterMemberMarketingVehicleTypeSeries[] {
    if (months.length === 0 || breakdown.length === 0) return [];

    const flat = items.map((it) => {
      const r = (it ?? {}) as RegisterMemberMonthlyVehicleTypeSeriesDto & Record<string, unknown>;
      return {
        year: Number(r.year ?? r['Year'] ?? 0),
        month: Number(r.month ?? r['Month'] ?? 0),
        vehicleTypeId: Number(r.vehicleTypeId ?? r['VehicleTypeId'] ?? 0),
        vehicleTypeName: String(r.vehicleTypeName ?? r['VehicleTypeName'] ?? 'ไม่ระบุประเภท'),
        total: Number(r.total ?? r['Total'] ?? 0),
      };
    });

    const maxPoint = Math.max(1, ...flat.map((x) => x.total), ...months.map((m) => m.paid));
    const n = months.length;

    return breakdown.map((vt) => {
      const points = months.map((m) => {
        const hit = flat.find(
          (f) =>
            f.year === m.year &&
            f.month === m.month &&
            f.vehicleTypeId === vt.vehicleTypeId &&
            f.vehicleTypeName === vt.vehicleTypeName,
        );
        const total = hit?.total ?? 0;
        return {
          year: m.year,
          month: m.month,
          label: m.label,
          total,
          totalPct: Math.round((total / maxPoint) * 100),
        };
      });

      const linePoints = points
        .map((p, i) => {
          const x = n === 1 ? 50 : (i / (n - 1)) * 100;
          const y = 92 - (p.total / maxPoint) * 78;
          return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .join(' ');

      return {
        vehicleTypeId: vt.vehicleTypeId,
        vehicleTypeName: vt.vehicleTypeName,
        color: vt.color,
        points,
        linePoints,
        total: vt.count,
      };
    });
  }

  private buildTrendInsight(series: RegisterMemberMarketingMonth[]): RegisterMemberMarketingTrendInsight {
    const empty: RegisterMemberMarketingTrendInsight = {
      direction: 'flat',
      label: 'ยังไม่มีแนวโน้ม',
      periodChangePercent: null,
      avgMomPercent: null,
      risingMonths: 0,
      fallingMonths: 0,
      flatMonths: 0,
      firstLabel: '',
      lastLabel: '',
      firstTotal: 0,
      lastTotal: 0,
      linePoints: '',
      lineDots: [],
    };

    if (series.length === 0) return empty;

    const completed = series.filter((m) => !m.isIncompleteMonth);
    const first = completed[0] ?? series[0];
    const last = completed[completed.length - 1] ?? series[0];
    const withMom = completed.filter((m) => m.momDelta !== null && m.trend !== 'pending');
    const risingMonths = withMom.filter((m) => m.trend === 'up').length;
    const fallingMonths = withMom.filter((m) => m.trend === 'down').length;
    const flatMonths = withMom.filter((m) => m.trend === 'flat').length;

    const momPercents = withMom
      .map((m) => m.momChangePercent)
      .filter((v): v is number => v !== null);
    const avgMomPercent =
      momPercents.length > 0
        ? Math.round((momPercents.reduce((a, b) => a + b, 0) / momPercents.length) * 10) / 10
        : null;

    const periodChangePercent =
      completed.length >= 2 ? this.calcChangePercent(first.paid, last.paid) : null;

    let direction: RegisterMemberTrendDirection = 'flat';
    let label = this.trendLabel(direction);

    if (completed.length < 2) {
      direction = series.some((m) => m.isIncompleteMonth) ? 'pending' : 'flat';
      label = direction === 'pending' ? 'เดือนปัจจุบันยังไม่จบ — ยังไม่สรุปแนวโน้ม' : 'ยังไม่มีแนวโน้ม';
    } else if (risingMonths > fallingMonths && risingMonths > flatMonths) {
      direction = 'up';
      label = this.trendLabel(direction);
    } else if (fallingMonths > risingMonths && fallingMonths > flatMonths) {
      direction = 'down';
      label = this.trendLabel(direction);
    } else if (periodChangePercent !== null) {
      direction = this.directionFromPercent(periodChangePercent);
      label = this.trendLabel(direction);
    }

    if (series.some((m) => m.isIncompleteMonth) && completed.length >= 2) {
      label = `${label} (ไม่นับเดือนปัจจุบัน)`;
    }

    const maxPaid = Math.max(1, ...series.map((m) => m.paid));
    const n = series.length;
    const lineDots = series.map((m, i) => {
      const x = n === 1 ? 50 : (i / (n - 1)) * 100;
      const y = 92 - (m.paid / maxPaid) * 78;
      return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
    });

    return {
      direction,
      label,
      periodChangePercent,
      avgMomPercent,
      risingMonths,
      fallingMonths,
      flatMonths,
      firstLabel: first.label,
      lastLabel: last.label,
      firstTotal: first.paid,
      lastTotal: last.paid,
      linePoints: lineDots.map((p) => `${p.x},${p.y}`).join(' '),
      lineDots,
    };
  }

  private buildKpiCards(
    summary: RegisterMemberMarketingSummary,
    vehicleTypes: RegisterMemberMarketingVehicleType[],
  ): RegisterMemberMarketingKpiCard[] {
    const mom =
      summary.momChangePercent === null
        ? undefined
        : `MoM ${summary.momChangePercent >= 0 ? '+' : ''}${summary.momChangePercent}%`;
    const topType = vehicleTypes[0];

    return [
      {
        title: 'ยอดชำระทั้งหมด',
        value: summary.paidCount.toLocaleString(),
        icon: 'payments',
        color: 'text-emerald-600',
        hint: mom,
      },
      {
        title: 'จำนวนประเภทรถ',
        value: vehicleTypes.length.toLocaleString(),
        icon: 'directions_car',
        color: 'text-sky-600',
      },
      {
        title: 'ประเภทสูงสุด',
        value: topType ? topType.count.toLocaleString() : '0',
        icon: 'emoji_events',
        color: 'text-amber-600',
        hint: topType?.vehicleTypeName,
      },
      {
        title: 'เดือนสูงสุด',
        value: summary.peakMonthTotal.toLocaleString(),
        icon: 'trending_up',
        color: 'text-teal-600',
        hint: summary.peakMonthLabel || undefined,
      },
    ];
  }

  private calcChangePercent(prev: number, curr: number): number {
    if (prev > 0) return Math.round(((curr - prev) * 1000) / prev) / 10;
    if (curr > 0) return 100;
    return 0;
  }

  private directionFromDelta(delta: number): RegisterMemberTrendDirection {
    if (delta > 0) return 'up';
    if (delta < 0) return 'down';
    return 'flat';
  }

  private directionFromPercent(pct: number): RegisterMemberTrendDirection {
    if (pct > 0.5) return 'up';
    if (pct < -0.5) return 'down';
    return 'flat';
  }

  private trendLabel(direction: RegisterMemberTrendDirection): string {
    if (direction === 'up') return 'แนวโน้มเพิ่มขึ้น';
    if (direction === 'down') return 'แนวโน้มลดลง';
    if (direction === 'pending') return 'เดือนปัจจุบันยังไม่จบ — ยังไม่สรุปแนวโน้ม';
    return 'แนวโน้มคงที่';
  }

  private isIncompleteCalendarMonth(year: number, month: number): boolean {
    const now = new Date();
    return year === now.getFullYear() && month === now.getMonth() + 1;
  }
}
